/**
 * Keep body-portaled pickers, overlay menus, the PDF editor and stacked dialogs
 * clickable inside a modal Radix Dialog or Sheet. FocusScope / DismissableLayer
 * otherwise steal pointer + focus and dismiss the parent (e.g. merge confirm,
 * spam confirm, a file preview while its PDF editor is open).
 *
 * The one list for every dialog: `DialogContent` and `SheetContent` compose it
 * automatically, so a dialog never passes its own copy.
 */

export interface DialogDismissGuardEvent {
  readonly preventDefault: () => void;
  readonly target: EventTarget | null;
}

/** A click or focus inside any of these never dismisses the dialog. */
const KEEP_OPEN_SELECTORS: readonly string[] = [
  "[data-emoji-picker-dropdown]",
  "[data-radix-popper-content-wrapper]",
  // Menus rendered outside the dialog body (date-picker caption, picklists).
  "[data-dialog-overlay-menu]",
  // Inbox compose dropdown panel, portaled outside `DialogContent`.
  "[data-inbox-compose-inline-select-panel]",
  // Nested dialog body (confirm / spam). Own overlay is not content — still closes.
  "[data-radix-dialog-content]",
  // Everything a dialog opened over this one renders (`DialogContent` nested host).
  "[data-dialog-nested-host]",
  // The PDF editor and its tool dialogs portal to `document.body`.
  "[data-pdf-editor-root]",
  "[data-pdf-editor-subdialog]",
  // Mobile call sheet / bar: answering a call must not close the open sheet.
  "[data-mobile-call-layer]",
  // Dictation floater: „Fertig“ would close the dialog and drop the text.
  "[data-global-speech-overlay]",
];

const PDF_EDITOR_ROOT_SELECTOR = "[data-pdf-editor-root]";

function pdfEditorMounted(): boolean {
  return (
    typeof document !== "undefined" &&
    document.querySelector(PDF_EDITOR_ROOT_SELECTOR) !== null
  );
}

export function preventDismissForPortaledPicker(
  event: DialogDismissGuardEvent,
): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  // A PDF-editor tool dialog portals into the editor root: its own overlay
  // sits inside that root and must still close it.
  if (
    target.closest("[data-radix-dialog-overlay]") !== null &&
    target.closest(PDF_EDITOR_ROOT_SELECTOR) !== null
  ) {
    return;
  }
  if (
    KEEP_OPEN_SELECTORS.some((selector) => target.closest(selector) !== null) ||
    // While the editor is mounted, keydown/focus often land on `body` (Konva
    // canvas) — `closest()` on the event cannot see the editor then.
    pdfEditorMounted()
  ) {
    event.preventDefault();
  }
}

/**
 * Generic over the concrete Radix event so the wrapped consumer keeps its own
 * signature — `onPointerDownOutside` and `onFocusOutside` hand out richer events
 * than the two fields this guard needs.
 */
export function composeDialogDismissGuard<
  TEvent extends DialogDismissGuardEvent,
>(consumer: ((event: TEvent) => void) | undefined): (event: TEvent) => void {
  return (event: TEvent): void => {
    preventDismissForPortaledPicker(event);
    consumer?.(event);
  };
}
